import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Save } from "lucide-react";
import { cmsApi } from "@/services/api/cmsApi";
import type { CMSContent } from "@/models/CMS";
import { Liquid } from "liquidjs";
import TinyMCEEditor from "./TinyMCEEditor";
import { LiquidDate } from "liquidjs/dist/util";

interface TemplateEditorProps {
  content: CMSContent;
}

const TemplateEditor = ({ content }: TemplateEditorProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [templateName, setTemplateName] = useState(content.name);
  const [htmlContent, setHtmlContent] = useState("");

  // Initialize Liquid engine
  const liquid = new Liquid();

  // Get content translations for the template
  const { data: translations, isLoading } = useQuery({
    queryKey: ["cms-content-translations", content.id],
    queryFn: () => cmsApi.getContentTranslations(content.id),
  });

  // Get the English translation
  const translation = translations?.find((t) => t.language_code === "en");

  // Default Liquid template
  const defaultLiquidTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{ document.title }}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
        .container { max-width: 800px; margin: 0 auto; }
        .header-content { text-align: center; margin-bottom: 30px; }
        .management-logo { max-height: 60px; margin-bottom: 15px; }
        .document-title { margin: 0; font-size: 24px; font-weight: bold; }
        .association-name { margin: 10px 0 0 0; color: #666; }
        .management-name { margin: 5px 0 0 0; font-size: 14px; font-weight: 600; }
        .document-date { margin: 5px 0 0 0; font-size: 14px; }
    </style>
</head>
<body>
   <div class="content" style="margin-bottom: 40px;border-top: 2px solid black;padding: 20px;">
  {% for section in sections %}
  <div class="section" style="margin-bottom: 30px;">
    <h2 style="font-size: 18px; font-weight: bold; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 15px;">{{ section.title }}</h2>
    
    <div class="fields" style="display: table; width: 100%;">
      {% for field in section.fields %}
      <div class="field-row" style="display: table-row; border-bottom: 1px solid #f0f0f0;">
        <div class="field-label" style="display: table-cell; padding: 8px 20px 8px 0; font-weight: 600; width: 40%; vertical-align: top;">{{ field.label }}:</div>
        <div class="field-value" style="display: table-cell; padding: 8px 0; vertical-align: top;">{{ field.value }}</div>
      </div>
      {% endfor %}
    </div>
  </div>
  {% endfor %}
</div>
</div>
</body>
</html>`;

  // Load template on page load (showing variable names, not rendered data)
  useEffect(() => {
    const loadTemplate = () => {
      let templateToShow = defaultLiquidTemplate;

      // If we have existing content, use it
      if (translation?.html_content) {
        templateToShow = translation.html_content;
      }

      // Show the template with variable names preserved
      setHtmlContent(templateToShow);
    };

    if (translations !== undefined) {
      loadTemplate();
    }
  }, [translation?.html_content, translations]);

  const updateContent = useMutation({
    mutationFn: ({
      name,
      htmlContent,
    }: {
      name: string;
      htmlContent: string;
    }) =>
      Promise.all([
        cmsApi.updateContent(content.id, { name }),
        cmsApi.upsertTranslation(content.id, "en", {
          title: name,
          html_content: htmlContent,
          content: htmlContent,
        }),
      ]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms-content-tree"] });
      queryClient.invalidateQueries({
        queryKey: ["cms-content-translations", content.id],
      });
      toast({
        title: "Template saved",
        description: "Your template has been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error saving template",
        description: error.message || "Failed to save template",
      });
    },
  });

  const handleSave = () => {
    updateContent.mutate({
      name: templateName,
      htmlContent,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading template...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-7xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Template Editor</CardTitle>
                <CardDescription>
                  Edit your rendered template content using TinyMCE - the
                  industry-standard editor that preserves all HTML styling
                </CardDescription>
              </div>
              <Button onClick={handleSave} disabled={updateContent.isPending}>
                <Save className="mr-2 h-4 w-4" />
                {updateContent.isPending ? "Saving..." : "Save Template"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Estoppel Certificate, PDF Document Template"
              />
            </div>

            <div className="space-y-2">
              <Label>Template Content</Label>
              <TinyMCEEditor value={htmlContent} onChange={setHtmlContent} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TemplateEditor;
